import { Box, Tooltip } from "@mui/material";
import { USER_NEEDS, USER_NEEDS_GROUPS, GROUP_OUTLINES } from "./userNeedsConstants.js";

const s = (val, svgSize) => svgSize * (val / 660);

function getCircularLayout(needs, center, radius) {
  const N = needs.length;
  return needs.map((n, i) => {
    const angle = (2 * Math.PI * i) / N - Math.PI / 2;
    return {
      ...n,
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
      angle: (angle * 180) / Math.PI,
    };
  });
}

export default function UserNeedsOrbitChart({
  needs,
  scores = {},
  dominant,
  showGroupLabels = true,
  svgSize = 660
}) {
  const center = svgSize / 2;
  const orbitRadius = svgSize * 0.36;            // ~238 at 660
  const innerOrbitRadius = svgSize * 0.235;      // ~155 at 660
  const backgroundRadius = svgSize * 0.5;        // 330 at 660
  const isNews = needs.length === 4;

  // Responsive icon/text sizing
  const nodeRadius = s(30, svgSize);
  const iconSize = s(36, svgSize);
  const nodeScoreFont = Math.max(s(22, svgSize), 10);
  const nodeLabelFont = Math.max(s(17, svgSize), 10);
  const groupLabelFont = Math.max(s(21, svgSize), 10);
  const groupTitleFont = Math.max(s(13, svgSize), 8);
  const centerNodeRadius = s(80, svgSize);
  const centerMainFont = Math.max(s(29, svgSize), 12);
  const centerSubFont = Math.max(s(14, svgSize), 10);

  // Layout
  const anchorPositions = getCircularLayout(USER_NEEDS, center, orbitRadius);
  const needsKeys = new Set(needs.map(n => n.key));

  // Compute top score(s); still honor an explicit `dominant`
  const scoreEntries = Object.entries(scores || {}).filter(([k]) => needsKeys.has(k));
  const maxScore = scoreEntries.length ? Math.max(...scoreEntries.map(([, v]) => v)) : null;
  const topKeys = new Set(
    maxScore == null ? [] : scoreEntries.filter(([, v]) => v === maxScore).map(([k]) => k)
  );
  const dominantKey = dominant ?? (topKeys.size ? [...topKeys][0] : null);

  // For ellipses/labels: group by group from all 8 anchor positions
  const allGroupPositions = {};
  anchorPositions.forEach(n => {
    if (!allGroupPositions[n.group]) allGroupPositions[n.group] = [];
    allGroupPositions[n.group].push({ x: n.x, y: n.y });
  });

  // For plotting nodes: group only those present in props
  const activeGroupPositions = {};
  anchorPositions.forEach(n => {
    if (!needsKeys.has(n.key)) return;
    if (!activeGroupPositions[n.group]) activeGroupPositions[n.group] = [];
    activeGroupPositions[n.group].push({ x: n.x, y: n.y });
  });

  // For group ellipses/labels, always use *allGroupPositions*:
  const groupEllipses = Object.entries(allGroupPositions)
    .map(([group, pts]) => {
      const cx = pts.reduce((a, b) => a + b.x, 0) / pts.length;
      const cy = pts.reduce((a, b) => a + b.y, 0) / pts.length;
      const rx = Math.max(...pts.map(p => Math.abs(p.x - cx))) + s(65, svgSize);
      const ry = Math.max(...pts.map(p => Math.abs(p.y - cy))) + s(55, svgSize);
      return { group, cx, cy, rx, ry };
    });

  return (
    <Box sx={{
      position: "relative",
      width: "100%",
      maxWidth: svgSize,
      mx: "auto",
      mb: 2
    }}>
      <svg
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        width="100%"
        height="auto"
        style={{ display: "block" }}
      >
        {/* Effects */}
        <defs>
          <filter id="soft-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={s(6, svgSize)} result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Large dark background */}
        <circle cx={center} cy={center} r={backgroundRadius} fill="#171923" opacity={1} />
        {/* Radial lines */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = (2 * Math.PI * i) / 8;
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={center + backgroundRadius * Math.cos(angle)}
              y2={center + backgroundRadius * Math.sin(angle)}
              stroke="#edeaff"
              strokeWidth={s(1.1, svgSize)}
              opacity={0.09}
            />
          );
        })}
        {/* Faint background circles */}
        <circle cx={center} cy={center} r={backgroundRadius - s(40, svgSize)} fill="none" stroke="#edeaff" strokeWidth={s(1.2, svgSize)} opacity={0.12} />
        <circle cx={center} cy={center} r={backgroundRadius - s(85, svgSize)} fill="none" stroke="#edeaff" strokeWidth={s(1.1, svgSize)} opacity={0.08} />
        <circle cx={center} cy={center} r={orbitRadius - s(55, svgSize)} fill="none" stroke="#edeaff" strokeWidth={s(0.9, svgSize)} opacity={0.06} />
        <circle cx={center} cy={center} r={orbitRadius - s(85, svgSize)} fill="none" stroke="#edeaff" strokeWidth={s(0.8, svgSize)} opacity={0.045} />
        {/* inner faint orbit */}
        <circle
          cx={center}
          cy={center}
          r={innerOrbitRadius}
          stroke="#edeaff"
          strokeWidth={s(2.2, svgSize)}
          fill="none"
          opacity={0.13}
        />
        {/* Main faint orbit */}
        <circle cx={center} cy={center} r={orbitRadius} stroke="#edeaff" strokeWidth={s(2.8, svgSize)} fill="none" opacity={0.10} />

        {/* Group ellipses and labels */}
        {groupEllipses.map(({ group, cx, cy, rx, ry }) => {
          const groupObj = USER_NEEDS_GROUPS[group];
          let labelPos = { x: cx, y: cy };
          return (
            <g key={group}>
              <ellipse
                cx={cx}
                cy={cy}
                rx={rx}
                ry={ry}
                stroke={GROUP_OUTLINES[group]}
                strokeWidth={s(4.3, svgSize)}
                fill="none"
                opacity={0.30}
                style={{ filter: `blur(1.7px)` }}
              />
              {showGroupLabels && (
                <>
                  <text
                    x={labelPos.x}
                    y={labelPos.y - s(8, svgSize)}
                    textAnchor="middle"
                    fontWeight="bold"
                    fontSize={groupLabelFont}
                    fill={groupObj.color}
                    opacity={0.50}
                    style={{
                      letterSpacing: 1.2,
                      textShadow: "0 2px 32px #000b, 0 0px 4px #000a"
                    }}
                  >
                    {groupObj.label}
                  </text>
                  <text
                    x={labelPos.x}
                    y={labelPos.y + s(10, svgSize)}
                    textAnchor="middle"
                    fontWeight={500}
                    fontSize={groupTitleFont}
                    fill={groupObj.color}
                    opacity={0.50}
                    style={{
                      textShadow: "0 1px 16px #000b, 0 1px 2px #0009"
                    }}
                  >
                    {groupObj.title}
                  </text>
                </>
              )}
            </g>
          );
        })}

        {/* User need nodes */}
        {anchorPositions.map((p) => {
          if (!needsKeys.has(p.key)) return null; // Only plot if present in props
          const node = needs.find(n => n.key === p.key) || p; // Use label/color/icon from props if present
          const sc = scores?.[p.key] ?? 0;

          const isTop = topKeys.has(p.key) || (dominantKey && dominantKey === p.key);
          const baseR = nodeRadius;
          const nodeR = isTop ? baseR * 1.28 : baseR;
          const strokeW = isTop ? s(7.5, svgSize) : s(2.3, svgSize);

          return (
            <g key={p.key}>
              {/* Beam from center for top nodes */}
              {isTop && (
                <line
                  x1={center} y1={center} x2={p.x} y2={p.y}
                  stroke={node.color}
                  strokeWidth={s(2.2, svgSize)}
                  opacity={0.28}
                />
              )}

              {/* Pulsing halo ring for top nodes */}
              {isTop && (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={nodeR * 1.45}
                  fill="none"
                  stroke={node.color}
                  strokeWidth={s(3.0, svgSize)}
                  opacity={0.45}
                  filter="url(#soft-glow)"
                >
                  <animate
                    attributeName="r"
                    values={`${nodeR * 1.2};${nodeR * 1.6};${nodeR * 1.2}`}
                    dur="2.2s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.35;0.08;0.35"
                    dur="2.2s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}

              {/* Score */}
              <text
                x={p.x}
                y={p.y - s(46, svgSize)}
                textAnchor="middle"
                fontWeight="bold"
                fontSize={isTop ? nodeScoreFont * 1.25 : nodeScoreFont}
                fill={node.color}
                style={{ textShadow: "0 1px 20px #222,0 2px 24px #fff" }}
              >
                {sc}
              </text>

              {/* Main node */}
              <circle
                cx={p.x}
                cy={p.y}
                r={nodeR}
                fill={node.color}
                stroke={node.color}
                strokeWidth={strokeW}
                style={{
                  filter: isTop ? `drop-shadow(0 0 18px ${node.color}aa)` : undefined,
                  transition: "stroke-width 0.2s, r 0.2s"
                }}
              />

              {/* Icon */}
              <foreignObject x={p.x - iconSize / 2} y={p.y - iconSize / 2} width={iconSize} height={iconSize}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1, minWidth: s(190, svgSize) }}>
                      <div style={{ fontWeight: 700, color: node.color, marginBottom: 2 }}>{node.label}</div>
                      <div style={{ fontSize: s(14, svgSize), lineHeight: 1.4, color: "#FFF" }}>{node.description}</div>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: iconSize, height: iconSize, color: "#fff"
                  }}>{node.icon}</Box>
                </Tooltip>
              </foreignObject>

              {/* Label */}
              <text
                x={p.x}
                y={p.y + s(50, svgSize)}
                textAnchor="middle"
                fontWeight={isTop ? 900 : "bold"}
                fontSize={nodeLabelFont}
                fill="#fff"
                style={{ textShadow: "0 1px 8px #000b" }}
              >
                {node.label}
              </text>
            </g>
          );
        })}

        {/* Central node */}
        <circle cx={center} cy={center} r={centerNodeRadius} fill="#fff" stroke="#edeaff" strokeWidth={s(2.7, svgSize)} />
        <text
          x={center}
          y={center - s(2, svgSize)}
          textAnchor="middle"
          fontWeight="bold"
          fontSize={centerMainFont}
          fill="#7c41fd"
        >
          user needs
        </text>
        <text
          x={center}
          y={center + s(22, svgSize)}
          textAnchor="middle"
          fontSize={centerSubFont}
          fill="#000"
          opacity={0.7}
        >
          {isNews ? "for news" : "model 2.0"}
        </text>
      </svg>
    </Box>
  );
}
